import React, { useState, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import MaterialTable from "material-table";
import airportData from "../data.json";
import moment from "moment";
import SegmentDetail from "../SegmentDetailKiwi";
import {
  convertHM,
  getDateDifference,
  TimeFormatting,
} from "../../../helpers/date.helper";
import { getCommision } from "../../../helpers/general.helper";
import "./index.css";
import FlightDetail from "./FlightDetail";
import SelectedFlightMobile from "../SelectedFlightMobile";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Paper,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import Hidden from "@material-ui/core/Hidden";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";

const useStyles = makeStyles((theme) => ({
  stoppage: {
    fontSize: "8px !important",
  },
  fareOptionPaper: {
    padding: theme.spacing(2),
    borderRadius: 12,
    minWidth: 260,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e0e0e0",
    margin: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    background: "#fff",
  },
  fareOptionHeader: {
    fontWeight: 700,
    fontSize: 20,
    marginBottom: 8,
  },
  fareOptionBadge: {
    background: "#ffe082",
    color: "#795548",
    borderRadius: 8,
    padding: "2px 10px",
    fontWeight: 700,
    fontSize: 12,
    marginLeft: 8,
  },
  fareOptionList: {
    margin: "8px 0",
    padding: 0,
    listStyle: "none",
    width: "100%",
  },
  fareOptionListItem: {
    display: "flex",
    alignItems: "center",
    fontSize: 15,
    marginBottom: 4,
  },
  fareOptionCheck: {
    color: "#43a047",
    marginRight: 6,
    fontSize: 18,
  },
  fareOptionCross: {
    color: "#e53935",
    marginRight: 6,
    fontSize: 18,
  },
  bookBtn: {
    marginTop: 16,
    width: "100%",
    fontWeight: 700,
    borderRadius: 8,
  },
  modalGrid: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
}));

function getFlightKey(flight) {
  const seg = flight.slices[0].segments[0];
  return `${seg.operating_carrier || seg.airline}_${seg.flight_number}_${
    seg.local_departure
  }_${seg.local_arrival}`;
}

function DataTable(props) {
  console.log("DEBUG DataTable - Received props:", {
    journeyType: props.journeyType,
    dataLength: props.data?.length,
    firstFlight: props.data?.[0],
    isReturn: props.journeyType === "Return",
  });

  const classes = useStyles();
  const [state, setState] = React.useState({
    columns: undefined,
    data: undefined,
  });
  const [showDetail, setShowDetail] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOptions, setModalOptions] = useState([]);
  const [modalFlight, setModalFlight] = useState(null);

  // For two-way selection
  const [selectedDepart, setSelectedDepart] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);

  // For two-way paired card UI
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [modalCombo, setModalCombo] = useState(null);

  // Split flights for two-way
  let departingFlights = [];
  let returningFlights = [];
  if (props.journeyType === "Return" && props.data) {
    departingFlights = props.data.filter((f) => f.isOutbound);
    returningFlights = props.data.filter((f) => f.isInbound);

    console.log("DEBUG DataTable - Two-way flights:", {
      departingCount: departingFlights.length,
      returningCount: returningFlights.length,
      sampleDeparting: departingFlights[0],
      sampleReturning: returningFlights[0],
    });
  }

  // Map: flightKey -> all fares for that flight
  const fareMap = React.useMemo(() => {
    const map = {};
    (props.data || []).forEach((flight) => {
      const key = getFlightKey(flight);
      if (!map[key]) map[key] = [];
      map[key].push(flight);
    });
    return map;
  }, [props.data]);

  // Reset modal state when journeyType changes
  useEffect(() => {
    setModalOpen(false);
    setModalOptions([]);
    setModalFlight(null);
  }, [props.journeyType]);

  const toggleDrawer = (open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setDrawerOpen(open);
  };

  const handleRowClick = (e, row) => {
    e.stopPropagation();
    return props?.onFlightSelected(row);
  };

  const handleViewMore = (flight) => {
    if (!flight || !flight.slices || flight.slices.length < 1) return;
    const key = getFlightKey(flight);
    const fareOptions = (props.data || []).filter(
      (f) => getFlightKey(f) === key
    );
    setModalOptions(fareOptions);
    setModalFlight(flight);
    setModalOpen(true);
  };

  const handleBookNow = (flight) => {
    setModalOpen(false);
    if (props.onFlightSelected) {
      props.onFlightSelected(flight);
    }
  };

  const columns = [
    {
      title: "Icon",
      field: "icon",
      render: (flight) =>
        flight.slices.map((airport) => (
          <div style={{ textAlign: "center" }}>
            <img
              title={
                Object.keys(airport.airline).length > 1
                  ? "Multiple Airlines"
                  : airportData[
                      airport.segments[0].operating_carrier
                        ? airport.segments[0].operating_carrier
                        : airport.segments[0].airline
                    ]?.name
              }
              height="50px"
              width="50px"
              src={
                Object.keys(airport.airline).length > 1
                  ? "https://www.gstatic.com/flights/airline_logos/70px/multi.png"
                  : `https://images.kiwi.com/airlines/64/${
                      airport.segments[0].operating_carrier
                        ? airport.segments[0].operating_carrier
                        : airport.segments[0].airline
                    }.png`
              }
            />
          </div>
        )),
    },
    {
      title: "Timings",
      field: "timings",
      render: (flight) =>
        flight.slices.map((airport) => (
          <div style={{ width: "250px" }}>
            <Typography
              variant="h6"
              style={{
                color: "var(--darker)",
                textTransform: "capitalize",
                lineHeight: "20px",
              }}
            >
              {TimeFormatting(airport.segments[0].local_departure)} -{" "}
              {TimeFormatting(
                airport.segments[airport.segments.length - 1].local_arrival
              )}
            </Typography>

            <Typography
              component="span"
              style={{
                color: "var(--darker)",
                textTransform: "capitalize",
                fontSize: "14px",
              }}
              className="clipped-label"
            >
              {flight.virtual_interlining && <span>.</span>}
              {Object.keys(airport.airline).length > 1
                ? Object.keys(airport.airline).map((a, index) => {
                    return (index !== 0 ? ", " : "") + airportData[a]?.name;
                  })
                : airportData[
                    airport.segments[0].operating_carrier
                      ? airport.segments[0].operating_carrier
                      : airport.segments[0].airline
                  ]?.name}
            </Typography>
          </div>
        )),
    },

    {
      title: "Duration",
      field: "duration",
      render: (flight) =>
        flight.slices.map((airport) => (
          <Hidden smDown implementation="css">
            <Typography
              component="p"
              style={{ color: "var(--darker)", textTransform: "capitalize" }}
            >
              {convertHM(airport.duration)}
            </Typography>
          </Hidden>
        )),
    },
    {
      title: "stops",
      width: 100,
      render: (flight) =>
        flight.slices.map((airport) => (
          <Hidden smDown implementation="css">
            <Typography component="p" style={{ color: "var(--darker)" }}>
              {airport.segments.length > 2
                ? `${airport.segments.length - 1} Stops`
                : ""}
              {airport.segments.length == 2 ? `1 Stop` : ""}
              {airport.segments.length == 1 ? `Nonstop` : ""}
            </Typography>
            {airport.segments.length > 1 &&
              airport.segments.map((segment, index) => {
                if (index !== airport.segments.length - 1) {
                  return (
                    <Typography
                      component="p"
                      style={{
                        color: "var(--darker)",
                        textTransform: "capitalize",
                      }}
                    >
                      {getDateDifference(
                        airport.segments[index + 1].utc_departure,
                        segment.utc_arrival
                      ) +
                        " " +
                        segment.flyTo}
                    </Typography>
                  );
                }
              })}
          </Hidden>
        )),
    },

    {
      title: "Baggage",
      field: "baggage",

      render: (flight) => {
        console.log("flight|inside datatable", flight);

        return (
          <>
            <div style={{ width: "200px" }}></div>
            <Typography
              component="p"
              style={{
                color: "var(--darker)",
                textTransform: "capitalize",
                fontSize: 14,
              }}
            >
              {flight.baglimit && flight.baglimit.hand_weight
                ? `${flight.baglimit.hand_weight} Hand Bag`
                : "7 Kg Hand Bag"}
            </Typography>
            <Typography
              component="p"
              style={{
                color: "var(--darker)",
                textTransform: "capitalize",
                fontSize: 14,
              }}
            >
              {" "}
              {flight.baglimit && flight.baglimit.hold_weight
                ? `${flight.baglimit.hold_weight} Check-In Bag`
                : "15 Kg Check-In Bag"}
            </Typography>
          </>
        );
      },
    },
    {
      title: "Pricing",
      field: "pricing",
      width: 150,
      render: (flight) => (
        <div style={{ width: "200px" }}>
          {" "}
          <Typography
            variant="h5"
            style={{
              color: "var(--darker)",
              textTransform: "capitalize",
              fontWeight: "bold",
            }}
          >
            {" "}
            {props.currency ? props.currency : "Rs"}{" "}
            {flight.price +
              getCommision(
                flight.price,
                props.vendorCommission.type,
                props.vendorCommission.value
              ) +
              getCommision(
                flight.price,
                props.adminCommission.type,
                props.adminCommission.value
              )}
          </Typography>{" "}
        </div>
      ),
    },
  ];
  console.log({ veera: props });

  // --- Two-way UI ---
  if (props.journeyType === "Return" && props.data) {
    return (
      <div style={{ background: "#f7fafd", borderRadius: 16, padding: 24 }}>
        <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 18 }}>
          {props.departureAirportName} → {props.arrivalAirportName} & Return (
          {props.data.length} options)
        </Typography>
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {props.data.map((flight, idx) => {
            if (!flight.slices || flight.slices.length < 2) return null;
            const depart = flight.slices[0];
            const ret = flight.slices[1];
            const totalPrice =
              flight.price +
              getCommision(
                flight.price,
                props.vendorCommission.type,
                props.vendorCommission.value
              ) +
              getCommision(
                flight.price,
                props.adminCommission.type,
                props.adminCommission.value
              );
            return (
              <Paper
                key={idx}
                elevation={3}
                style={{
                  borderRadius: 16,
                  padding: 24,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "stretch",
                  gap: 24,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <Typography
                      variant="subtitle2"
                      style={{
                        fontWeight: 700,
                        color: "#129880",
                        marginBottom: 4,
                      }}
                    >
                      DEPART FLIGHT
                    </Typography>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 16 }}
                    >
                      <img
                        src={`https://images.kiwi.com/airlines/64/${
                          depart.segments[0].operating_carrier ||
                          depart.segments[0].airline
                        }.png`}
                        alt={
                          depart.segments[0].operating_carrier ||
                          depart.segments[0].airline
                        }
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 8,
                          marginRight: 8,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 700, fontSize: 16 }}
                        >
                          {TimeFormatting(depart.segments[0].local_departure)} -{" "}
                          {TimeFormatting(
                            depart.segments[depart.segments.length - 1]
                              .local_arrival
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ color: "#888", fontWeight: 500 }}
                        >
                          {airportData[
                            depart.segments[0].operating_carrier ||
                              depart.segments[0].airline
                          ]?.name ||
                            depart.segments[0].operating_carrier ||
                            depart.segments[0].airline}
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {convertHM(depart.duration)} Min •{" "}
                          {depart.segments.length === 1
                            ? "Nonstop"
                            : `${depart.segments.length - 1} Stop`}{" "}
                          • {flight.baglimit?.hand_weight || "7 KG Hand Bag"}
                          {flight.baglimit?.hold_weight
                            ? `, ${flight.baglimit.hold_weight}`
                            : ", 15 KG Check-In Bag"}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      borderTop: "1px dashed #e0e0e0",
                      paddingTop: 8,
                      marginTop: 8,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      style={{
                        fontWeight: 700,
                        color: "#129880",
                        marginBottom: 4,
                      }}
                    >
                      RETURN FLIGHT
                    </Typography>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 16 }}
                    >
                      <img
                        src={`https://images.kiwi.com/airlines/64/${
                          ret.segments[0].operating_carrier ||
                          ret.segments[0].airline
                        }.png`}
                        alt={
                          ret.segments[0].operating_carrier ||
                          ret.segments[0].airline
                        }
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 8,
                          marginRight: 8,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 700, fontSize: 16 }}
                        >
                          {TimeFormatting(ret.segments[0].local_departure)} -{" "}
                          {TimeFormatting(
                            ret.segments[ret.segments.length - 1].local_arrival
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ color: "#888", fontWeight: 500 }}
                        >
                          {airportData[
                            ret.segments[0].operating_carrier ||
                              ret.segments[0].airline
                          ]?.name ||
                            ret.segments[0].operating_carrier ||
                            ret.segments[0].airline}
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {convertHM(ret.duration)} Min •{" "}
                          {ret.segments.length === 1
                            ? "Nonstop"
                            : `${ret.segments.length - 1} Stop`}{" "}
                          • {flight.baglimit?.hand_weight || "7 KG Hand Bag"}
                          {flight.baglimit?.hold_weight
                            ? `, ${flight.baglimit.hold_weight}`
                            : ", 15 KG Check-In Bag"}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    minWidth: 180,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    gap: 16,
                  }}
                >
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: 700,
                      color: "#222",
                      fontSize: 22,
                      marginBottom: 8,
                    }}
                  >
                    {props.currency || "INR"} {totalPrice}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ borderRadius: 8, fontWeight: 700, width: 120 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (props.onFlightSelected)
                        props.onFlightSelected(flight);
                    }}
                  >
                    Book Now
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    style={{ borderRadius: 8, fontWeight: 700, width: 120 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewMore(flight);
                    }}
                  >
                    View More
                  </Button>
                </div>
              </Paper>
            );
          })}
        </div>
        {/* Unified Modal for Fare Options */}
        <Dialog
          open={modalOpen && !!modalFlight}
          onClose={() => setModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            style={{ fontWeight: 700, fontSize: 22, textAlign: "center" }}
          >
            Other Fare Options
          </DialogTitle>
          <DialogContent>
            {modalFlight && (
              <div style={{ marginBottom: 18 }}>
                {modalFlight.slices.map((slice, idx) => {
                  if (!slice) return null;
                  const depSeg = slice.segments[0];
                  const arrSeg = slice.segments[slice.segments.length - 1];
                  return (
                    <div
                      key={idx}
                      style={{
                        background: "#f8fafd",
                        borderRadius: 10,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                        padding: "10px 16px",
                        marginBottom: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        fontSize: 14,
                      }}
                    >
                      <img
                        src={`https://images.kiwi.com/airlines/64/${
                          depSeg.operating_carrier || depSeg.airline
                        }.png`}
                        alt={depSeg.airline}
                        width={32}
                        height={32}
                        style={{
                          borderRadius: 6,
                          background: "#fff",
                          marginRight: 8,
                        }}
                      />
                      <div style={{ minWidth: 90 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>
                          {depSeg.airline || depSeg.operating_carrier}
                        </div>
                        <div
                          style={{
                            fontWeight: 600,
                            color: "#222",
                            fontSize: 15,
                          }}
                        >
                          {TimeFormatting(depSeg.local_departure)} –{" "}
                          {TimeFormatting(arrSeg.local_arrival)}
                        </div>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span style={{ color: "#7b8a99", fontWeight: 500 }}>
                            {TimeFormatting(depSeg.local_departure)},{" "}
                            {depSeg.cityFrom || depSeg.flyFrom} (
                            {depSeg.flyFrom}) → {arrSeg.cityTo || arrSeg.flyTo}{" "}
                            ({arrSeg.flyTo})
                          </span>
                          <span
                            style={{
                              color: "#129880",
                              fontWeight: 600,
                              marginLeft: 8,
                            }}
                          >
                            {convertHM(slice.duration)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                            marginTop: 2,
                          }}
                        >
                          <span style={{ color: "#7b8a99", fontWeight: 500 }}>
                            {TimeFormatting(arrSeg.local_arrival)}, Arrive at{" "}
                            {arrSeg.cityTo || arrSeg.flyTo} ({arrSeg.flyTo})
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                justifyContent: "center",
              }}
            >
              {modalOptions.map((flight, idx) => {
                if (!flight) return null;
                const fareName =
                  flight.fare_name || flight.fareType || "Standard";
                return (
                  <Paper
                    key={idx}
                    elevation={2}
                    style={{
                      padding: 16,
                      borderRadius: 10,
                      minWidth: 220,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      background: "#fff",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      style={{ fontWeight: 700, marginBottom: 6 }}
                    >
                      {props.currency || "INR"} {flight.price}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: 600, marginBottom: 8 }}
                    >
                      {fareName}
                    </Typography>
                    <ul
                      style={{
                        margin: "8px 0",
                        padding: 0,
                        listStyle: "none",
                        width: "100%",
                      }}
                    >
                      <li
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: 15,
                          marginBottom: 4,
                        }}
                      >
                        7 Kgs Cabin Baggage
                      </li>
                      <li
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: 15,
                          marginBottom: 4,
                        }}
                      >
                        15 Kgs Check-in Baggage
                      </li>
                      <li
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: 15,
                          marginBottom: 4,
                        }}
                      >
                        Free Date Change up to 2 hrs before departure
                      </li>
                      <li
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: 15,
                          marginBottom: 4,
                          color: "#e53935",
                        }}
                      >
                        Chargeable Seats
                      </li>
                      <li
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: 15,
                          marginBottom: 4,
                          color: "#e53935",
                        }}
                      >
                        Chargeable Meals
                      </li>
                    </ul>
                    <Button
                      variant="contained"
                      color="primary"
                      style={{
                        marginTop: 12,
                        width: "100%",
                        fontWeight: 700,
                        borderRadius: 8,
                      }}
                      onClick={() => handleBookNow(flight)}
                    >
                      Book Now
                    </Button>
                  </Paper>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <>
      <div className="desktop-list">
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 4 }}>
            <Typography
              variant="h6"
              style={{
                color: "var(--darker)",
                textTransform: "capitalize",
                textAlign: "left",
              }}
            >
              {" "}
              Best Departing Flights
            </Typography>
          </div>
          <div style={{ flex: 1, maxWidth: "180px" }}>
            <div> {props.graphComponent && props.graphComponent}</div>
          </div>
        </div>

        <div
          style={{
            padding: "10px 0px",
            borderRadius: "5px",

            marginTop: "15px",
          }}
        >
          {props.data &&
            props.data.map((flight, idx) => {
              // Only show outbound flights for one-way
              if (flight.isInbound) return null;
              const key = getFlightKey(flight);
              const faresForFlight = fareMap[key];
              return (
                <Grid
                  key={idx}
                  container
                  spacing={1}
                  alignItems="center"
                  style={{
                    margin: "10px 0px",
                    padding: "12px 0",
                    borderRadius: 8,
                    background: "#fff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    cursor: "pointer",
                    transition: "border 0.2s, background 0.2s",
                  }}
                  onClick={(e) => handleRowClick(e, flight)}
                  tabIndex={0}
                  aria-label={`Select flight ${TimeFormatting(
                    flight.slices[0].segments[0].local_departure
                  )} to ${TimeFormatting(
                    flight.slices[0].segments[
                      flight.slices[0].segments.length - 1
                    ].local_arrival
                  )}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRowClick(e, flight);
                  }}
                >
                  {/* Airline Logo and Time */}
                  <Grid
                    item
                    xs={3}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "left",
                    }}
                  >
                    <img
                      src={
                        Object.keys(flight.slices[0].airline).length > 1
                          ? "https://www.gstatic.com/flights/airline_logos/70px/multi.png"
                          : `https://images.kiwi.com/airlines/64/${
                              flight.slices[0].segments[0].operating_carrier ||
                              flight.slices[0].segments[0].airline
                            }.png`
                      }
                      alt={
                        flight.slices[0].segments[0].operating_carrier ||
                        flight.slices[0].segments[0].airline
                      }
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        marginRight: 15,
                        marginLeft: 15,
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "left",
                      }}
                    >
                      <Typography
                        variant="h6"
                        style={{
                          fontWeight: 700,
                          fontSize: 18,
                          marginBottom: 2,
                          textAlign: "left",
                        }}
                      >
                        {TimeFormatting(
                          flight.slices[0].segments[0].local_departure
                        )}{" "}
                        -{" "}
                        {TimeFormatting(
                          flight.slices[0].segments[
                            flight.slices[0].segments.length - 1
                          ].local_arrival
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          color: "#888",
                          fontWeight: 500,
                          textAlign: "left",
                        }}
                      >
                        {airportData[
                          flight.slices[0].segments[0].operating_carrier ||
                            flight.slices[0].segments[0].airline
                        ]?.name ||
                          flight.slices[0].segments[0].operating_carrier ||
                          flight.slices[0].segments[0].airline}
                      </Typography>
                    </div>
                  </Grid>
                  {/* Flight Info */}

                  {/* Duration, Stops, Baggage */}
                  <Grid
                    item
                    xs={2}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "left",
                    }}
                  >
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {convertHM(flight.slices[0].duration)} Min
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={2}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "left",
                    }}
                  >
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {flight.slices[0].segments.length === 1
                        ? "Nonstop"
                        : `${flight.slices[0].segments.length - 1} Stop`}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "left",
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 500, textAlign: "left" }}
                    >
                      {flight.baglimit?.hand_weight || "7 KG Hand Bag"}{" "}
                      {flight.baglimit?.hold_weight
                        ? `, ${flight.baglimit.hold_weight}`
                        : ", 15 KG Check-In Bag"}
                    </Typography>
                  </Grid>
                  {/* Price and View More Button */}
                  <Grid
                    item
                    xs={2}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 12,
                    }}
                  >
                    <Typography
                      variant="h6"
                      style={{ fontWeight: 700, color: "#222", fontSize: 20 }}
                    >
                      {props.currency || "INR"}{" "}
                      {flight.price +
                        getCommision(
                          flight.price,
                          props.vendorCommission.type,
                          props.vendorCommission.value
                        ) +
                        getCommision(
                          flight.price,
                          props.adminCommission.type,
                          props.adminCommission.value
                        )}
                    </Typography>
                    {faresForFlight.length > 1 && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        style={{ borderRadius: 8, fontWeight: 700 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewMore(flight);
                        }}
                      >
                        View More
                      </Button>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 12,
                    }}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      style={{ borderRadius: 8, fontWeight: 700 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(e, flight);
                      }}
                    >
                      <ExpandMoreIcon />
                    </Button>
                  </Grid>
                </Grid>
              );
            })}
        </div>
      </div>
      <div className="mobile-list">
        <Typography
          variant="h6"
          style={{
            color: "var(--darker)",
            textTransform: "capitalize",
            textAlign: "left",
          }}
        >
          {" "}
          Best Departing Flights
        </Typography>
        <div
          style={{
            padding: "10px 0px",
            borderRadius: "5px",
            backgroundColor: "var(--white)",
            marginTop: "15px",
          }}
        >
          {props.data &&
            props.data.map((flight) => (
              <Grid
                onClick={(e) => handleRowClick(e, flight)}
                container
                spacing={1}
                style={{
                  borderBottom: "1px solid var(--dark)",
                  margin: "10px 0px",
                }}
              >
                <Grid item xs="9">
                  <Grid container>
                    <Grid item xs="2" sm="2">
                      {flight.slices.map((airport) => (
                        <div style={{ textAlign: "left" }}>
                          <img
                            title={
                              Object.keys(airport?.airline).length > 1
                                ? "Multiple Airlines"
                                : airportData[
                                    airport.segments[0].operating_carrier
                                      ? airport.segments[0].operating_carrier
                                      : airport.segments[0].airline
                                  ]
                                ? airportData[
                                    airport.segments[0].operating_carrier
                                      ? airport.segments[0].operating_carrier
                                      : airport.segments[0].airline
                                  ].name
                                : ""
                            }
                            height="30px"
                            width="30px"
                            src={
                              Object.keys(airport.airline).length > 1
                                ? "https://www.gstatic.com/flights/airline_logos/70px/multi.png"
                                : `https://images.kiwi.com/airlines/64/${
                                    airport.segments[0].operating_carrier
                                      ? airport.segments[0].operating_carrier
                                      : airport.segments[0].airline
                                  }.png`
                            }
                          />
                        </div>
                      ))}
                    </Grid>
                    <Grid item xs="10" sm="10">
                      {flight.slices.map((airport) => (
                        <div style={{ textAlign: "left" }}>
                          <Typography
                            component="p"
                            style={{
                              color: "var(--darker)",
                              textTransform: "capitalize",
                              lineHeight: "20px",
                              fontWeight: "bold",
                            }}
                          >
                            {TimeFormatting(
                              airport.segments[0].local_departure
                            )}{" "}
                            -{" "}
                            {TimeFormatting(
                              airport.segments[airport.segments.length - 1]
                                .local_arrival
                            )}
                          </Typography>
                          <Typography
                            component="span"
                            style={{
                              color: "var(--darker)",
                              textTransform: "capitalize",
                              fontSize: "16px",
                            }}
                            className="clipped-label"
                          >
                            {Object.keys(airport.airline).length > 1
                              ? Object.keys(airport.airline).map((a, index) => {
                                  return (
                                    (index !== 0 ? ", " : "") +
                                    airportData[a]?.name
                                  );
                                })
                              : airportData[
                                  airport.segments[0].operating_carrier
                                    ? airport.segments[0].operating_carrier
                                    : airport.segments[0].airline
                                ]?.name}
                          </Typography>
                        </div>
                      ))}
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs="2"></Grid>
                    <Grid item xs="10">
                      {flight.slices.map((airport) => (
                        <Typography
                          component="p"
                          style={{
                            color: "var(--darker)",
                            textTransform: "capitalize",
                            fontSize: "12px",
                            textAlign: "left",
                          }}
                        >
                          {convertHM(airport.duration)}
                        </Typography>
                      ))}
                    </Grid>
                    <Grid item xs="2"></Grid>
                    <Hidden smDown implementation="css">
                      <Grid item xs="10">
                        <div style={{ textAlign: "left" }}>
                          {flight.slices.map((airport) => (
                            <>
                              <Typography
                                component="p"
                                style={{
                                  color: "var(--darker)",
                                  textTransform: "capitalize",
                                }}
                              >
                                {airport.segments.length > 2
                                  ? `${airport.segments.length - 1} Stops`
                                  : ""}
                                {airport.segments.length == 1 ? `Nonstop` : ""}
                              </Typography>
                              {airport.segments.length > 1 &&
                                airport.segments.map((segment, index) => {
                                  if (index !== airport.segments.length - 1) {
                                    return (
                                      <Typography
                                        component="p"
                                        style={{
                                          color: "var(--darker)",
                                          textTransform: "capitalize",
                                          fontSize: "6px",
                                        }}
                                      >
                                        {getDateDifference(
                                          airport.segments[index + 1]
                                            .utc_departure,
                                          segment.utc_arrival
                                        ) +
                                          " " +
                                          segment.flyTo}
                                      </Typography>
                                    );
                                  }
                                })}
                            </>
                          ))}
                        </div>
                      </Grid>
                    </Hidden>
                  </Grid>
                </Grid>
                <Grid item xs="3">
                  <Typography
                    component="span"
                    style={{ color: "var(--darker)" }}
                  >
                    {props?.currency || "INR"}
                  </Typography>
                  <Typography
                    component="span"
                    style={{
                      color: "var(--darker)",
                      textTransform: "capitalize",
                      fontWeight: "bold",
                    }}
                  >
                    {flight.price +
                      getCommision(
                        flight.price,
                        props.vendorCommission.type,
                        props.vendorCommission.value
                      ) +
                      getCommision(
                        flight.price,
                        props.adminCommission.type,
                        props.adminCommission.value
                      )}
                  </Typography>
                  <div style={{ marginTop: "10px" }}>
                    <a
                      style={{
                        padding: "8px",
                        backgroundColor: "var(--backgroundcolor)",
                        color: "var(--darker)",
                        borderRadius: "50%",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFlight(flight);
                        setDrawerOpen(true);
                      }}
                    >
                      {drawerOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </a>
                  </div>
                </Grid>
              </Grid>
            ))}
        </div>
      </div>
      {/* Unified Modal for Fare Options */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{ fontWeight: 700, fontSize: 22, textAlign: "center" }}
        >
          Other Fare Options
        </DialogTitle>
        <DialogContent>
          {/* Flight details section above fare options */}
          {modalOptions.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              {modalOptions[0].slices.map((slice, idx) => {
                const depSeg = slice.segments[0];
                const arrSeg = slice.segments[slice.segments.length - 1];
                return (
                  <div
                    key={idx}
                    style={{
                      background: "#f8fafd",
                      borderRadius: 10,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                      padding: "10px 16px",
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      fontSize: 14,
                    }}
                  >
                    {/* Airline logo, name, time range */}
                    <img
                      src={`https://images.kiwi.com/airlines/64/${
                        depSeg.operating_carrier || depSeg.airline
                      }.png`}
                      alt={depSeg.airline}
                      width={32}
                      height={32}
                      style={{
                        borderRadius: 6,
                        background: "#fff",
                        marginRight: 8,
                      }}
                    />
                    <div style={{ minWidth: 90 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>
                        {depSeg.airline || depSeg.operating_carrier}
                      </div>
                      <div
                        style={{ fontWeight: 600, color: "#222", fontSize: 15 }}
                      >
                        {TimeFormatting(depSeg.local_departure)} –{" "}
                        {TimeFormatting(arrSeg.local_arrival)}
                      </div>
                    </div>
                    {/* Departure/Arrival, duration, features */}
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ color: "#7b8a99", fontWeight: 500 }}>
                          {TimeFormatting(depSeg.local_departure)},{" "}
                          {depSeg.cityFrom || depSeg.flyFrom} ({depSeg.flyFrom})
                          → {arrSeg.cityTo || arrSeg.flyTo} ({arrSeg.flyTo})
                        </span>
                        <span
                          style={{
                            color: "#129880",
                            fontWeight: 600,
                            marginLeft: 8,
                          }}
                        >
                          {convertHM(slice.duration)}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                          marginTop: 2,
                        }}
                      >
                        <span style={{ color: "#7b8a99", fontWeight: 500 }}>
                          {TimeFormatting(arrSeg.local_arrival)}, Arrive at{" "}
                          {arrSeg.cityTo || arrSeg.flyTo} ({arrSeg.flyTo})
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className={classes.modalGrid}>
            {modalOptions.map((flight, idx) => {
              const seg = flight.slices[0].segments[0];
              const fareName =
                flight.fare_name || flight.fareType || "Standard";
              // Example: you can add more details as needed
              return (
                <Paper
                  className={classes.fareOptionPaper}
                  key={idx}
                  elevation={3}
                >
                  <Typography className={classes.fareOptionHeader}>
                    {props.currency || "INR"} {flight.price}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, marginBottom: 8 }}
                  >
                    {fareName}
                  </Typography>
                  {flight.badge && (
                    <span className={classes.fareOptionBadge}>
                      {flight.badge}
                    </span>
                  )}
                  <ul className={classes.fareOptionList}>
                    <li className={classes.fareOptionListItem}>
                      <CheckCircleIcon className={classes.fareOptionCheck} />7
                      Kgs Cabin Baggage
                    </li>
                    <li className={classes.fareOptionListItem}>
                      <CheckCircleIcon className={classes.fareOptionCheck} />
                      15 Kgs Check-in Baggage
                    </li>
                    <li className={classes.fareOptionListItem}>
                      <CheckCircleIcon className={classes.fareOptionCheck} />
                      Free Date Change up to 2 hrs before departure
                    </li>
                    <li className={classes.fareOptionListItem}>
                      <CancelIcon className={classes.fareOptionCross} />
                      Chargeable Seats
                    </li>
                    <li className={classes.fareOptionListItem}>
                      <CancelIcon className={classes.fareOptionCross} />
                      Chargeable Meals
                    </li>
                  </ul>
                  {/* Add more details as needed, e.g. badges, offers, etc. */}
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.bookBtn}
                    onClick={() => handleBookNow(flight)}
                  >
                    Book Now
                  </Button>
                </Paper>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default React.memo(DataTable);
DataTable.defaultProps = {
  title: "DataTable",
  data: [],
  columns: [
    {
      title: "Icon",
      field: "icon",
      render: (flight) =>
        flight.slices.map((airport) => (
          <>
            <div style={{ textAlign: "left" }}>
              <img
                title={
                  Object.keys(airport.airline).length > 1
                    ? "Multiple Airlines"
                    : airportData[
                        airport.segments[0].operating_carrier
                          ? airport.segments[0].operating_carrier
                          : airport.segments[0].airline
                      ].name
                }
                height="50px"
                width="50px"
                src={
                  Object.keys(airport.airline).length > 1
                    ? "https://www.gstatic.com/flights/airline_logos/70px/multi.png"
                    : `https://images.kiwi.com/airlines/64/${
                        airport.segments[0].operating_carrier
                          ? airport.segments[0].operating_carrier
                          : airport.segments[0].airline
                      }.png`
                }
              />
            </div>
            {flight.virtual_interlining && <span>.</span>}
          </>
        )),
    },
    {
      title: "Timings",
      field: "timings",
      render: (flight) =>
        flight.slices.map((airport) => (
          <div style={{ width: "250px", textAlign: "left" }}>
            {" "}
            <p style={{ fontSize: 20, marginBottom: 0 }} className="bold">
              {TimeFormatting(airport.segments[0].local_departure)} -{" "}
              {TimeFormatting(
                airport.segments[airport.segments.length - 1].local_arrival
              )}
            </p>
            <p style={{ margin: 0 }}>
              {Object.keys(airport.airline).length > 1
                ? Object.keys(airport.airline).map((a, index) => {
                    return (index !== 0 ? ", " : "") + airportData[a]?.name;
                  })
                : airportData[
                    airport.segments[0].operating_carrier
                      ? airport.segments[0].operating_carrier
                      : airport.segments[0].airline
                  ]?.name}
            </p>
          </div>
        )),
    },

    {
      title: "Duration",
      field: "duration",
      render: (flight) =>
        flight.slices.map((airport) => (
          <p className="bold" style={{ margin: 0 }}>
            {convertHM(airport.duration)}
          </p>
        )),
    },
    {
      title: "stops",
      width: 100,
      render: (flight) =>
        flight.slices.map((airport) => (
          <>
            <p className="bold mb0">
              {airport.segments.length > 2
                ? `${airport.segments.length - 1} Stops`
                : ""}
              {airport.segments.length == 1 ? `Nonstop` : ""}
            </p>
            {airport.segments.length > 1 &&
              airport.segments.map((segment, index) => {
                if (index !== airport.segments.length - 1) {
                  return (
                    <p style={{ margin: 0, fontSize: 12 }}>
                      {getDateDifference(
                        airport.segments[index + 1].utc_departure,
                        segment.utc_arrival
                      ) +
                        " " +
                        segment.flyTo}
                    </p>
                  );
                }
              })}
          </>
        )),
    },

    {
      title: "Baggage",
      field: "baggage",
      render: (flight) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <p style={{ margin: 0 }}>
            {flight.baglimit && flight.baglimit.hand_weight
              ? `${flight.baglimit.hand_weight} Kg Hand Bag`
              : "7 Kg Hand Bag"}
          </p>
          <p style={{ margin: 0 }}>
            {" "}
            {flight.baglimit.hold_weight
              ? `${flight.baglimit.hold_weight} Kg Check-In Bag`
              : "15 Kg Check-In Baag"}
          </p>
        </div>
      ),
    },
    {
      title: "Pricing",
      field: "pricing",
      render: (flight) => (
        <p
          style={{
            color: "#5B8038",
            fontSize: 25,
            fontWeight: "bold",
            margin: 0,
          }}
        >
          {" "}
          {localStorage.getItem("selectedCurrency")
            ? localStorage.getItem("selectedCurrency")
            : "Rs"}{" "}
          {flight.price}
        </p>
      ),
    },
  ],
};
